import { Form as AntdForm } from "antd";
import {
  Children, cloneElement, isValidElement, useEffect,
  useMemo,
} from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { NAMESPACES } from "@/constants/i18next.constants";

import { FormItemStyled } from "./FormItem.styles";
import { TFieldErrorWithOptions, TMarginSize } from "./FormItem.types";

type AntdFormItemProps = React.ComponentProps<typeof AntdForm.Item>;

type FormItemProps<TFieldValues extends FieldValues = FieldValues> = {
  children: React.ReactNode;
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  disabled?: boolean;
  marginSize?: TMarginSize;
} & Omit<AntdFormItemProps, "name" | "rules" | "validateStatus">;

// TODO: Support `onBlur` `ref` `reset`
function FormItem<TFieldValues extends FieldValues = FieldValues>({
  children,
  control,
  name,
  disabled,
  help,
  valuePropName,
  marginSize = "middle",
  ...props
}: FormItemProps<TFieldValues>) {
  const { t } = useTranslation(NAMESPACES.VALIDATION);
  const { field, fieldState } = useController({
    name,
    control,
    disabled,
  });
  const { setFieldValue } = AntdForm.useFormInstance();

  useEffect(() => {
    setFieldValue(name, field.value);
  }, [
    setFieldValue,
    name,
    field.value,
  ]);

  const helpMessage = useMemo(() => {
    if (!fieldState || !fieldState.error || !fieldState.error.message) {
      return help;
    }

    const { message } = fieldState.error;

    if ((message as unknown as TFieldErrorWithOptions).key) {
      // @ts-expect-error Note: Burayı daha iyi ele alabiliriz.
      return t(message.key, message.options);
    }
    else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return t(message as any);
    }
  }
  , [fieldState,
    t,
    help]);

  return (
    <FormItemStyled
      {...props}
      name={name}
      initialValue={field.value}
      validateStatus={fieldState.invalid
        ? "error"
        : undefined}
      help={helpMessage}
      $marginSize={marginSize}
    >
      {Children.map(
        children,
        child => isValidElement(child)
        && cloneElement(child, {
          ...field,
          // @ts-expect-error Ant Design form item name type safe is not necessary here
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange: (...args: any) => {
            child.props.onChange?.(...args);
            field.onChange(...args);
          },
          onBlur: () => {
            child.props.onBlur?.();
            field.onBlur();
          },
          ...valuePropName && {
            [valuePropName]: field.value,
          },
        }),
      )}
    </FormItemStyled>
  );
}

export default FormItem;
